import { dataService } from '../../../services/data.services';

const ASSOCIATION_CACHE_VERSION = 'v2';
const staffUserCache = new Map();
const staffAssociationCache = new Map();

export const isAdminCommunicationRole = (userType) => ['institution-admin', 'institution-user', 'branch-user'].includes(userType);

const getAssociationAcademicYearId = (association = {}) => (
    association.academic_year?.academic_year_id
    || association.academic_year_id
    || association.academic_year?._id
    || ''
);

export const getStaffAssociationContext = async (branch_id, email) => {
    const cacheKey = `${ASSOCIATION_CACHE_VERSION}::${branch_id}::${email}`;
    if (staffUserCache.has(cacheKey)) {
        return { userData: staffUserCache.get(cacheKey) };
    }

    const userRes = await dataService.GetAPI(`users/${email}?branch_id=${branch_id}`);
    const userData = userRes.data?.data?.data || {};
    staffUserCache.set(cacheKey, userData);

    return { userData };
};

export const getStaffAssociationsForAcademicYears = async (branch_id) => {
    const cacheKey = `${ASSOCIATION_CACHE_VERSION}::${branch_id}::all-years`;

    if (staffAssociationCache.has(cacheKey)) {
        return staffAssociationCache.get(cacheKey);
    }

    const assocRes = await dataService.GetAPI(`associations/my-associations?branch_id=${branch_id}`);
    const directAssociations = assocRes.data?.data?.data?.associations || [];
    staffAssociationCache.set(cacheKey, directAssociations);
    return directAssociations;
};

export const filterAcademicYearsByAssociations = (academicYears = [], associations = []) => {
    const assignedYearIds = new Set(
        associations
            .map((association) => getAssociationAcademicYearId(association))
            .filter(Boolean)
            .map(String)
    );

    return academicYears.filter((year) =>
        assignedYearIds.has(String(year._id))
    );
};

export const filterClassesByAssociations = (classes = [], associations = [], academicYearId) => {
    const filteredAssociations = academicYearId
        ? associations.filter((association) => String(getAssociationAcademicYearId(association)) === String(academicYearId))
        : associations;

    const assignedClassIds = new Set(
        filteredAssociations
            .map((association) => association.class_id)
            .filter(Boolean)
            .map(String)
    );

    return classes.filter((classItem) => assignedClassIds.has(String(classItem._id)));
};

const getAssociationSectionLabel = (association = {}) => {
    const baseName = association.section_name || association.name || '';
    if (association?.section_status === false && !String(baseName).includes('(Inactive)')) {
        return `${baseName} (Inactive)`.trim();
    }
    return baseName;
};

export const getSectionsByAssociations = (associations = [], academicYearId, classId) => {
    return [...new Map(
        associations
            .filter((association) =>
                (!academicYearId || String(getAssociationAcademicYearId(association)) === String(academicYearId)) &&
                (!classId || String(association.class_id) === String(classId))
            )
            .map((association) => [association.section_id, {
                _id: association.section_id,
                name: getAssociationSectionLabel(association),
                section_id: association.section_id,
                section_name: association.section_name,
                section_status: association.section_status,
            }])
    ).values()];
};

export const getActiveSectionsByAssociations = async (branch_id, associations = [], academicYearId, classId) => {
    const filteredSections = getSectionsByAssociations(associations, academicYearId, classId);
    if (!filteredSections.length) return [];

    const sectionsRes = await dataService.GetAPI(
        `sections?branch_id=${branch_id}&filter=true&status=true&role=class-association`
    );
    const activeSections = sectionsRes.data?.data?.data || [];
    const activeSectionMap = new Map(
        activeSections.map((section) => [String(section?._id || section?.id), section])
    );

    return filteredSections
        .filter((section) => activeSectionMap.has(String(section._id)))
        .map((section) => {
            const matchedSection = activeSectionMap.get(String(section._id)) || {};
            return {
                ...section,
                name: matchedSection.name || section.name,
                section_name: matchedSection.name || section.section_name || section.name,
            };
        });
};

export const getSubjectsByAssociations = (associations = [], academicYearId, classId, sectionId) => {
    return [...new Map(
        associations
            .filter((association) =>
                (!academicYearId || String(getAssociationAcademicYearId(association)) === String(academicYearId)) &&
                (!classId || String(association.class_id) === String(classId)) &&
                (!sectionId || sectionId === 'all' || String(association.section_id) === String(sectionId))
            )
            .flatMap((association) => association.subjects || [])
            .filter((subject) => subject?.subject_id)
            .map((subject) => [subject.subject_id, {
                _id: subject.subject_id,
                name: subject.subject_name,
            }])
    ).values()];
};
